/**
 * RocketFuel Abo (subscribe and save). See snippets/rf-abo.liquid for the rules.
 *
 * One state for the whole cart: cart attributes _rf_abo ('1'/'0') and _rf_abo_w (weeks).
 * - <rf-abo-toggle> (product page + cart drawer) switches it.
 * - On the product page the hidden selling_plan input rides along with the add to cart.
 * - sync() brings the cart in line with the state in one go: one request removes the
 *   lines that change and saves the state, a second adds them back (with or without the
 *   plan) and re-renders the cart. Shopify bundles cannot carry a plan, so in Abo mode they are added as
 *   their boxes (merged with the same boxes already in the cart, remembered in the cart attribute
 *   _rf_abo_bundles), and switching off puts the bundle back with its own discount.
 * - Not combinable with the box builder ('Mix 4, pay 3', one-time purchases only): an add
 *   from the builder (source 'rf-box-builder') switches the Abo off, so the free box stays.
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const cfgEl = document.getElementById('rf-abo-config');
const CFG = cfgEl ? JSON.parse(cfgEl.textContent) : null;
const ROOT = ((CFG && CFG.root) || '/').replace(/\/?$/, '/');
const WEEKS = [4, 6, 8, 10];
const SOURCE = 'rf-abo';
const BUILDER_SOURCE = 'rf-box-builder';

/* Picked in this tab (switch touched by the shopper): kept while the cart is still empty,
   so turning it on and then browsing to the next product keeps it. A cart that was emptied
   or left from an earlier visit does not come back with the Abo on (CFG.stale). */
const CHOSEN_KEY = 'rfAboChosen';
function chosen(value) {
  try {
    if (value === undefined) return sessionStorage.getItem(CHOSEN_KEY) === '1';
    sessionStorage.setItem(CHOSEN_KEY, value ? '1' : '0');
  } catch (e) {}
  return false;
}
const KEEP = !!(CFG && CFG.stale && chosen());

const state = {
  on: !!(CFG && (CFG.on || KEEP)),
  weeks: WEEKS.includes(Number(CFG && CFG.weeks)) ? Number(CFG.weeks) : 6,
};

/* count: boxes already on the subscription in the cart (24 Oct 2026). The product page adds them to
   its own boxes for the 5 / 10 / 15 % tier, the same as the cart does (tierPct(ABO_COUNT + qty)). */
window.rfAboState = { on: state.on, weeks: state.weeks, count: state.on ? Number(CFG && CFG.count) || 0 : 0 };

/* Sale guard (snippets/rf-abo.liquid): a box on sale (compare-at above price) never goes on the
   Abo, or the sale price would stay in the subscription for good. One in the cart (also inside
   a bundle) switches the Abo off; the drawer then says why. */
const SALE = new Set(((CFG && CFG.sale) || []).filter(Boolean).map(String));
function hasSale(data) {
  if (!SALE.size || !data || !data.lines) return false;
  return data.lines.some(
    (l) => SALE.has(String(l.variant_id)) || (l.components || []).some((c) => SALE.has(String(c.variant_id)))
  );
}

let queue = Promise.resolve();
const enqueue = (fn) => {
  queue = queue.then(fn).catch((e) => console.warn('[rf-abo]', e));
  return queue;
};

/* ---------- helpers ---------- */

function parseWeeks(plan) {
  const text = [plan.name, ...(plan.opts || [])].filter(Boolean).join(' ');
  const m = text.match(/(\d+)\s*-?\s*(week|woche|wk)/i);
  return m ? Number(m[1]) : null;
}

/* Plans by box count on the subscription (Shopify Subscriptions, one plan per tier, each with
   4 / 6 / 8 / 10 weeks): 1 box 5 %, 2 boxes 10 %, 3+ boxes 15 %. The volume discount is part
   of the plan price, so checkout shows the real price for every later delivery. */
function tierPct(boxes) {
  return boxes >= 3 ? 15 : boxes === 2 ? 10 : 5;
}
let ABO_COUNT = Number(CFG && CFG.count) || 0;

/* weeks -> plan id, for the tier: the plan with the highest percentage up to the tier's (so a
   missing 10 / 15 % plan falls back to the 5 % one instead of breaking the switch). */
function planMap(plans, pct) {
  const want = pct || 5;
  const best = {};
  (plans || []).filter(Boolean).forEach((p) => {
    const w = parseWeeks(p);
    if (!w) return;
    const v = Number(p.pct) || 0;
    const cur = best[w];
    const fits = v <= want;
    if (!cur) best[w] = { id: p.id, v, fits };
    else if (fits && (!cur.fits || v > cur.v)) best[w] = { id: p.id, v, fits };
    else if (!fits && !cur.fits && v < cur.v) best[w] = { id: p.id, v, fits };
  });
  const map = {};
  Object.keys(best).forEach((w) => (map[w] = best[w].id));
  return map;
}

/* ---------- adds while the Abo is on ----------
   Quick add, upsell and the like add a box without a plan; sync() then swapped it, so the
   drawer first showed the one-time price and refreshed a moment later. Instead the plan goes
   along with the add itself (variants and plans from the config, rendered by Liquid). Box
   builder items (_rf_mix4, one-time only) and products without a plan are left alone; if
   Shopify refuses the plan, the add is sent again as it was. */
const VARIANT_PLANS = {};
Object.keys((CFG && CFG.variants) || {}).forEach((vid) => {
  const list = ((CFG.variants[vid] || [])).filter(Boolean).map((p) => ({ ...p, opts: (p.opts || []).filter(Boolean) }));
  if (Object.keys(planMap(list)).length) VARIANT_PLANS[vid] = list;
});
/* The plan for an add: the boxes already on the subscription plus the ones added decide the
   tier (sync() corrects every line afterwards anyway). */
function planFor(variantId, qty) {
  const list = VARIANT_PLANS[String(variantId)];
  if (!list) return null;
  return planMap(list, tierPct(ABO_COUNT + (Number(qty) || 1)))[state.weeks] || null;
}
function withPlan(body) {
  if (typeof body === 'string') {
    let json;
    try {
      json = JSON.parse(body);
    } catch (e) {
      return null;
    }
    const items = Array.isArray(json.items) ? json.items : json.id ? [json] : [];
    let changed = false;
    items.forEach((it) => {
      if (it.selling_plan || (it.properties && it.properties._rf_mix4)) return;
      const sp = planFor(it.id, it.quantity);
      if (sp) {
        it.selling_plan = sp;
        changed = true;
      }
    });
    return changed ? JSON.stringify(json) : null;
  }
  if (typeof FormData !== 'undefined' && body instanceof FormData) {
    if (!body.has('id') || body.get('selling_plan') || body.get('properties[_rf_mix4]')) return null;
    const sp = planFor(body.get('id'), body.get('quantity'));
    if (!sp) return null;
    const fd = new FormData();
    body.forEach((v, k) => fd.append(k, v));
    fd.set('selling_plan', String(sp));
    return fd;
  }
  return null;
}
if (CFG && window.fetch && !window.rfAboFetch) {
  window.rfAboFetch = true;
  const nativeFetch = window.fetch;
  window.fetch = function (input, init) {
    try {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      if (state.on && init && init.body && /\/cart\/add(\.js)?(\?|$)/.test(String(url))) {
        const body = withPlan(init.body);
        if (body) {
          const args = arguments;
          const self = this;
          return nativeFetch.call(self, input, { ...init, body }).then((res) => (res.ok ? res : nativeFetch.apply(self, args)));
        }
      }
    } catch (e) {}
    return nativeFetch.apply(this, arguments);
  };
}

/* Liquid gives line properties as [[name, value], ...] with HTML-escaped values. */
function decode(text) {
  const el = document.createElement('textarea');
  el.innerHTML = String(text);
  return el.value;
}

function toProps(raw) {
  const out = {};
  if (Array.isArray(raw)) {
    raw.forEach((pair) => {
      if (Array.isArray(pair) && pair.length >= 2 && pair[1] != null && pair[1] !== '') out[pair[0]] = decode(pair[1]);
    });
  } else if (raw && typeof raw === 'object') {
    Object.keys(raw).forEach((k) => {
      if (raw[k] != null && raw[k] !== '') out[k] = decode(raw[k]);
    });
  }
  return out;
}

async function readData() {
  // Never from a cache: an old copy has old line keys, and cart/change then fails ('no valid id').
  const res = await fetch(`${ROOT}?sections=rf-abo-cart&_=${Date.now()}`, { cache: 'no-store', headers: { Accept: 'application/json' } });
  const json = await res.json();
  const html = json['rf-abo-cart'] || '';
  const node = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-rf-abo-cart]');
  const data = JSON.parse(node.textContent);
  data.lines = (data.lines || []).filter(Boolean).map((l) => ({
    ...l,
    props: toProps(l.props),
    plans: (l.plans || []).filter(Boolean),
    components: (l.components || []).filter(Boolean).map((c) => ({ ...c, plans: (c.plans || []).filter(Boolean) })),
  }));
  return data;
}

/* Shopify puts every added line on top of the cart, one after the other, so a multi-item add shows up in reverse.
   Replacement lines go in reversed, so the cart keeps the order the shopper had (Raphael, 29 Oct 2026: the items
   changed places when the interval changed). Lines that do not change keep their place anyway. */
const inOrder = (items) => items.slice().reverse();

async function post(path, body) {
  const res = await fetch(`${ROOT}cart/${path}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const json = await res.json();
  if (!res.ok || json.status) throw new Error(json.description || json.message || `cart/${path} failed`);
  return json;
}

function hasProps(props) {
  return props && Object.keys(props).length > 0;
}

function lineItem(id, quantity, sellingPlan, props) {
  const item = { id: Number(id), quantity };
  if (sellingPlan) item.selling_plan = sellingPlan;
  if (hasProps(props)) item.properties = props;
  return item;
}

/* Bundles split for the Abo are remembered in the cart attribute _rf_abo_bundles
   ("bundleVariantId:count:boxId*per,boxId*per;..."), not as line properties. That way the
   boxes merge with the same boxes already in the cart (1 RACE from the bundle + 3 RACE = one
   line of 4), and switching the Abo off puts the bundle back with its own discount. */
function parseBundles(text) {
  const out = {};
  String(text || '')
    .split(';')
    .filter(Boolean)
    .forEach((rec) => {
      const [id, n, parts] = rec.split(':');
      if (!id || !parts) return;
      out[id] = {
        n: Number(n) || 0,
        parts: parts.split(',').map((x) => {
          const [vid, per] = x.split('*');
          return { vid: Number(vid), per: Number(per) || 1 };
        }),
      };
    });
  return out;
}

function formatBundles(map) {
  return Object.keys(map)
    .filter((id) => map[id].n > 0)
    .map((id) => `${id}:${map[id].n}:${map[id].parts.map((p) => `${p.vid}*${p.per}`).join(',')}`)
    .join(';');
}

/* Same box, same plan, same properties: one item, so the cart gets one line. */
function mergeItems(items) {
  const byKey = new Map();
  items.forEach((it) => {
    const key = `${it.id}|${it.selling_plan || ''}|${JSON.stringify(it.properties || {})}`;
    if (byKey.has(key)) byKey.get(key).quantity += it.quantity;
    else byKey.set(key, { ...it });
  });
  return [...byKey.values()];
}

/* What the cart should look like for the current state: lines to remove, items to add,
   and the new value of _rf_abo_bundles. */
function plan(data, bundlesText) {
  const remove = [];
  const add = [];
  const bundles = parseBundles(bundlesText);

  const swaps = []; // lines that only change their plan: one cart/change each, in place
  if (state.on) {
    // Boxes on the subscription once this is done (lines with a plan, bundles split into
    // their boxes): they pick the tier for every line.
    let boxes = 0;
    for (const l of data.lines) {
      if (planMap(l.plans)[state.weeks]) boxes += l.quantity;
      else if (l.components.length && l.components.every((c) => planMap(c.plans)[state.weeks])) {
        l.components.forEach((c) => (boxes += Math.max(1, Math.round(c.qty / l.quantity)) * l.quantity));
      }
    }
    const pct = tierPct(boxes);
    ABO_COUNT = boxes;
    announce();
    for (const l of data.lines) {
      const want = planMap(l.plans, pct)[state.weeks];
      if (want) {
        if (l.sp !== want) {
          remove.push(l);
          add.push(lineItem(l.variant_id, l.quantity, want, l.props));
          // Same line, other plan: cart/change swaps it in place (on, or a new interval).
          swaps.push({ key: l.key, quantity: l.quantity, sp: want });
        }
        continue;
      }
      // Shopify bundle: swap for its boxes, each with the plan (only if every box has one).
      if (l.components.length) {
        const parts = l.components.map((c) => ({
          c,
          per: Math.max(1, Math.round(c.qty / l.quantity)),
          sp: planMap(c.plans, pct)[state.weeks],
        }));
        if (!parts.every((p) => p.sp)) continue; // e.g. Trial Pack: single gels, no plan
        remove.push(l);
        parts.forEach((p) => add.push(lineItem(p.c.variant_id, p.per * l.quantity, p.sp, null)));
        const rec = bundles[l.variant_id] || { n: 0, parts: parts.map((p) => ({ vid: p.c.variant_id, per: p.per })) };
        rec.n += l.quantity;
        bundles[l.variant_id] = rec;
      }
    }
    return { remove, add: mergeItems(add), bundles: formatBundles(bundles), swaps };
  }

  // Off: boxes that came from a bundle become the bundle again (as many full sets as fit),
  // every other Abo line goes back to a one-time line.
  const handled = new Set();

  // Older carts: bundle boxes marked with the line property _rf_bundle.
  const groups = {};
  for (const l of data.lines) {
    const bundle = l.props && l.props._rf_bundle;
    if (!bundle) continue;
    const [bundleId, per] = String(bundle).split('|');
    (groups[bundleId] = groups[bundleId] || []).push({ l, per: Number(per) || 1 });
  }
  for (const bundleId of Object.keys(groups)) {
    const g = groups[bundleId];
    const n = Math.min(...g.map((x) => Math.floor(x.l.quantity / x.per)));
    g.forEach((x) => {
      handled.add(x.l.key);
      remove.push(x.l);
      const rest = x.l.quantity - Math.max(0, n) * x.per;
      if (rest > 0) add.push(lineItem(x.l.variant_id, rest, null, null));
    });
    if (n > 0) add.push(lineItem(bundleId, n, null, null));
  }

  // Bundles remembered in _rf_abo_bundles: as many as the Abo boxes in the cart still cover.
  const avail = {};
  for (const l of data.lines) {
    if (handled.has(l.key) || !l.sp) continue;
    avail[l.variant_id] = (avail[l.variant_id] || 0) + l.quantity;
  }
  const need = {};
  for (const id of Object.keys(bundles)) {
    const rec = bundles[id];
    const fit = Math.min(rec.n, ...rec.parts.map((p) => Math.floor((avail[p.vid] || 0) / p.per)));
    if (fit <= 0) continue;
    rec.parts.forEach((p) => {
      avail[p.vid] -= fit * p.per;
      need[p.vid] = (need[p.vid] || 0) + fit * p.per;
    });
    add.push(lineItem(id, fit, null, null));
  }

  for (const l of data.lines) {
    if (handled.has(l.key) || !l.sp) continue;
    remove.push(l);
    const take = Math.min(need[l.variant_id] || 0, l.quantity);
    if (take) need[l.variant_id] -= take;
    const rest = l.quantity - take;
    if (rest > 0) add.push(lineItem(l.variant_id, rest, null, l.props));
    // Not part of a bundle: cart/change drops the plan in place (selling_plan: null).
    if (!take) swaps.push({ key: l.key, quantity: l.quantity, sp: null });
  }
  return { remove, add: mergeItems(add), bundles: '', swaps };
}

/*
 * Bring the cart in line with the state: one read, then at most two writes.
 * 1. cart/update removes the lines that change and saves the state (cart attributes).
 * 2. cart/add adds their replacements and re-renders the cart sections in the same call.
 * Without lines to change, one cart/update saves the state and re-renders.
 * (Adding first does not work: Shopify re-keys the existing lines on every add.)
 * saveState: also write when no line has to change (switch, interval).
 */
function startReads() {
  return Promise.all([
    readData(),
    fetch(`${ROOT}cart.js?_=${Date.now()}`, { cache: 'no-store', headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .catch(() => null),
  ]);
}
/* Pointer down on the switch or an interval: start the two reads before the click lands
   (about 100 to 300 ms earlier). Used once, only if fresh and the cart did not change since. */
let prefetched = null;
function prefetchReads() {
  if (prefetched && Date.now() - prefetched.at < 2500) return;
  prefetched = { at: Date.now(), reads: startReads() };
  prefetched.reads.catch(() => (prefetched = null));
}
function takeReads() {
  const p = prefetched;
  prefetched = null;
  if (p && Date.now() - p.at < 4000) return p.reads.catch(() => startReads());
  return startReads();
}

async function sync(saveState) {
  // Both reads at once (one round trip instead of two), usually already started when the
  // finger / mouse went down on the switch (prefetchReads).
  let [data, before] = await takeReads();
  // The reads may be older than the last cart write (prefetched on pointer down while the
  // previous switch was still syncing). Line keys that the cart no longer has would make
  // cart/change fail with 'no valid id or line parameter': read again, fresh.
  const keysNow = new Set(((before && before.items) || []).map((it) => it.key));
  if (!before || data.lines.some((l) => !keysNow.has(l.key))) {
    [data, before] = await startReads();
  }
  if (state.on && hasSale(data)) {
    state.on = false;
    chosen(false);
    setDrawerOpen(false);
    saveState = true;
    paintAll();
  }
  const bundlesText = (before && before.attributes && before.attributes._rf_abo_bundles) || '';
  const { remove, add, bundles, swaps } = plan(data, bundlesText);
  if (!remove.length && !saveState && bundles === bundlesText) return false;
  const attributes = {
    _rf_abo: state.on ? '1' : '0',
    _rf_abo_w: String(state.weeks),
    _rf_abo_bundles: bundles,
  };
  // Remove the changing lines and add their replacements (bundles split or put back).
  const full = async (sections, rm, ad) => {
    const ups = {};
    rm.forEach((l) => (ups[l.key] = 0));
    if (!ad.length) {
      const body = { attributes, ...sections };
      if (rm.length) body.updates = ups;
      const cart = await post('update', body);
      return { cart, sections: cart.sections };
    }
    await post('update', { updates: ups, attributes });
    let res;
    let failed = null;
    try {
      res = await post('add', { items: inOrder(ad), ...sections });
    } catch (e) {
      // Put the original lines back so nothing is lost (e.g. a box went out of stock).
      failed = e;
      const back = rm.map((l) => lineItem(l.variant_id, l.quantity, l.sp, l.props));
      res = await post('add', { items: inOrder(back), ...sections }).catch(() =>
        // Last resort: the boxes without plan or properties, so the cart is never left empty.
        post('add', { items: inOrder(rm.map((l) => lineItem(l.variant_id, l.quantity, null, null))), ...sections })
      );
    }
    const cart = await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    return { cart, sections: res.sections, failed };
  };

  /* 20 Oct 2026: add first, then take the old lines out, so the cart is never empty in between (before: the update
     removed the lines and the add put them back, about a second with an empty cart, e.g. when checkout was opened
     in another tab). Shopify re-keys lines on every add, so the cart is read again and the old lines are found by
     what they are (box, plan, properties), not by key. Anything unexpected: the old way (full). */
  const propsKey = (props) => {
    const o = {};
    Object.keys(props || {}).sort().forEach((k) => { if (props[k] != null && props[k] !== '') o[k] = String(props[k]); });
    return JSON.stringify(o);
  };
  const ident = (variant, sp, props) => `${variant}|${sp || ''}|${propsKey(props)}`;
  const addFirst = async (sections, rm, ad) => {
    if (!ad.length) return full(sections, rm, ad);
    const minus = {};
    rm.forEach((l) => { const k = ident(l.variant_id, l.sp, l.props); minus[k] = (minus[k] || 0) + l.quantity; });
    await post('add', { items: inOrder(ad) });
    const now = await fetch(`${ROOT}cart.js?_=${Date.now()}`, { cache: 'no-store', headers: { Accept: 'application/json' } }).then((r) => r.json());
    const groups = {};
    (now.items || []).forEach((it) => {
      const sp = it.selling_plan_allocation && it.selling_plan_allocation.selling_plan ? it.selling_plan_allocation.selling_plan.id : '';
      const k = ident(it.variant_id, sp, it.properties);
      (groups[k] = groups[k] || []).push(it);
    });
    const ups = {};
    Object.keys(minus).forEach((k) => {
      const lines = groups[k] || [];
      const have = lines.reduce((n, it) => n + it.quantity, 0);
      if (have < minus[k]) throw new Error('rf-abo: old line not found');
      lines.forEach((it, i) => (ups[it.key] = i === 0 ? have - minus[k] : 0));
    });
    const cart = await post('update', { updates: ups, attributes, ...sections });
    return { cart, sections: cart.sections };
  };
  const safeFull = async (sections, rm, ad) => {
    try {
      return await addFirst(sections, rm, ad);
    } catch (e) {
      const [fresh, now] = await startReads();
      const again = plan(fresh, (now && now.attributes && now.attributes._rf_abo_bundles) || '');
      return full(sections, again.remove, again.add);
    }
  };

  await commit(async (sections) => {
    // Every changing line only swaps its plan (switch on/off or a new interval, no bundle to
    // split or restore): change the plan in place, one request per line, then one update for
    // the state and the sections. Instead of update + add + cart.js, and no line re-added.
    // Each cart write takes about half a second, so this only pays off for 1 or 2 lines.
    // Two lines of the same box (e.g. "Mix 4, pay 3": 3 paid + 1 free, split by the discount)
    // change each other's keys on the first cart/change, so the second one fails with 'no valid
    // id or line parameter'. Those go the long way: remove both, add them back as one line.
    const sameBox = new Set(remove.map((l) => l.variant_id)).size !== remove.length;
    if (!sameBox && swaps && swaps.length && swaps.length === remove.length && swaps.length <= 2 && bundles === bundlesText) {
      // One after the other: Shopify's cart writes are read-modify-write, so an attributes
      // update running at the same time can put back the old plan. The plan swaps first, then
      // the state attributes, which also re-render the drawer in the same call.
      try {
        for (let i = 0; i < swaps.length; i++) {
          await post('change', { id: swaps[i].key, quantity: swaps[i].quantity, selling_plan: swaps[i].sp });
        }
      } catch (e) {
        // A line changed meanwhile (stale key): read again and do it the long way.
        const [fresh, now] = await startReads();
        const again = plan(fresh, (now && now.attributes && now.attributes._rf_abo_bundles) || '');
        return safeFull(sections, again.remove, again.add);
      }
      const cart = await post('update', { attributes, ...sections });
      return { cart, sections: cart.sections };
    }
    return safeFull(sections, remove, add);
  });
  return true;
}

/*
 * Mix 4, pay 3: the bundle builder adds its 4 boxes with the line property _rf_mix4 (one id per
 * bundle) and applies MIX4 (1 bundle), MIX4X2 (2) or MIX4X3 (3+). Each code gives one free box
 * per bundle and combines with the Duo / Trio discounts on the other boxes. When bundle boxes
 * leave the cart, the code goes down with them, so nobody gets a free box without a bundle.
 */
function mix4Code(cart) {
  const per = {};
  ((cart && cart.items) || []).forEach((it) => {
    const id = it.properties && it.properties._rf_mix4;
    if (id) per[id] = (per[id] || 0) + it.quantity;
  });
  const n = Object.values(per).filter((q) => q >= 4).length;
  return n <= 0 ? '' : n === 1 ? 'MIX4' : n === 2 ? 'MIX4X2' : 'MIX4X3';
}

async function syncMix4() {
  const cart = await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
  const codes = (cart.discount_codes || []).map((d) => d.code).filter(Boolean);
  const mine = codes.filter((c) => /^MIX4/i.test(c));
  const want = mix4Code(cart);
  if (mine.length === (want ? 1 : 0) && (!want || mine[0].toUpperCase() === want)) return false;
  const next = codes.filter((c) => !/^MIX4/i.test(c));
  if (want) next.push(want);
  await commit(async (sections) => {
    const res = await post('update', {
      discount: next.join(','),
      attributes: { _rf_box_builder: want ? 'mix4' : '' },
      ...sections,
    });
    return { cart: res, sections: res.sections };
  });
  return true;
}

/* Runs the cart writes inside one CartLinesUpdateEvent, so the theme re-renders once. */
async function commit(run) {
  prefetched = null;
  const ids = Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
  const deferred = CartLinesUpdateEvent.createPromise();
  document.body.dispatchEvent(
    new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines: [], promise: deferred.promise })
  );
  let result;
  try {
    result = await run({ sections: ids.join(','), sections_url: window.location.pathname });
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(result.cart),
      detail: {
        sections: result.sections || undefined,
        items: result.cart.items,
        itemCount: result.cart.item_count,
        source: SOURCE,
        didError: false,
      },
    });
  } catch (e) {
    prefetched = null;
    deferred.reject(e);
    throw e;
  }
  // Reads prefetched while this write ran are out of date now.
  prefetched = null;
  if (result.failed) throw result.failed;
}

/* ---------- delivery estimate ---------- */

const DAY = 864e5;
// Same date style as the shipping box on the product page (assets/rf-shipping.js).
const T = {
  de: {
    wd: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
    mon: ['Jan', 'Feb', 'März', 'Apr', 'Mai', 'Juni', 'Juli', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'],
    day: (wd, d, mon) => `${wd} ${d}.${mon ? ' ' + mon : ''}`,
    first: 'Erste Lieferung',
    next: 'Nächste Lieferung',
  },
  en: {
    wd: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    mon: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    day: (wd, d, mon) => `${wd} ${d}${mon ? ' ' + mon : ''}`,
    first: 'First delivery',
    next: 'Next delivery',
  },
};
let shipData = null;
function loadShip() {
  if (shipData) return shipData;
  const node = document.querySelector('script[data-rf-ship-data]');
  if (node) {
    try {
      shipData = Promise.resolve(JSON.parse(node.textContent));
      return shipData;
    } catch (e) {}
  }
  shipData = fetch(`${ROOT}?sections=rf-abo-ship`, { headers: { Accept: 'application/json' } })
    .then((r) => r.json())
    .then((json) => {
      const el = new DOMParser().parseFromString(json['rf-abo-ship'] || '', 'text/html').querySelector('[data-rf-ship-data]');
      return JSON.parse(el.textContent);
    })
    .catch(() => null);
  return shipData;
}

/* Today (as a UTC midnight timestamp) and the minutes since midnight, both in the warehouse time zone. */
function warehouseNow(tz) {
  const parts = {};
  new Intl.DateTimeFormat('en-CA', {
    timeZone: tz || 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  })
    .formatToParts(new Date())
    .forEach((p) => (parts[p.type] = p.value));
  return { day: Date.UTC(+parts.year, +parts.month - 1, +parts.day), mins: +parts.hour * 60 + +parts.minute };
}

function estimate(ship, country, weeks, d) {
  const c = ship && ship.countries && ship.countries[country];
  if (!c || !d) return null;
  const holidays = new Set(ship.holidays || []);
  const shipDays = ship.shipDays || [1, 2, 3, 4, 5];
  const iso = (t) => new Date(t).toISOString().slice(0, 10);
  const dow = (t) => new Date(t).getUTCDay();
  const isShip = (t) => shipDays.includes(dow(t)) && !holidays.has(iso(t));
  // Transit days: Mon to Fri; German holidays only count for parcels inside Germany (same as the shipping box).
  const isWork = (t) => dow(t) >= 1 && dow(t) <= 5 && !(country === 'DE' && holidays.has(iso(t)));
  const [ch, cm] = String(ship.cutoff || '14:30').split(':').map(Number);
  const shipFrom = (day, mins) => {
    let t = day;
    if (!(isShip(t) && mins < ch * 60 + (cm || 0))) {
      do t += DAY; while (!isShip(t));
    }
    for (let i = 0; i < (ship.processing || 0); i++) {
      do t += DAY; while (!isShip(t));
    }
    return t;
  };
  const addWork = (t, n) => {
    let left = n;
    while (left > 0) {
      t += DAY;
      if (isWork(t)) left--;
    }
    return t;
  };
  const now = warehouseNow(ship.tz);
  const first = shipFrom(now.day, now.mins);
  // Shopify Subscriptions bills the next order exactly 'weeks' after this one, it ships from there.
  const second = shipFrom(now.day + weeks * 7 * DAY, now.mins);
  return {
    firstFrom: addWork(first, d[0]),
    firstTo: addWork(first, d[1]),
    nextFrom: addWork(second, d[0]),
    nextTo: addWork(second, d[1]),
    today: now.day,
  };
}

function formatEta(e, lang) {
  const t = T[lang] || T.en;
  const day = (ts, withMonth) => {
    const d = new Date(ts);
    // No weekday here: the two lines stay short ('7 to 12 Oct' / '7. bis 12. Okt'). No dash (7 Oct 2026).
    return t.day('', d.getUTCDate(), withMonth ? t.mon[d.getUTCMonth()] : '').trim();
  };
  const range = (a, b) => {
    if (a === b) return day(a, true);
    const sameMonth = new Date(a).getUTCMonth() === new Date(b).getUTCMonth();
    return `${day(a, !sameMonth)} ${lang === 'de' ? 'bis' : 'to'} ${day(b, true)}`;
  };
  return [
    [t.first, range(e.firstFrom, e.firstTo)],
    [t.next, range(e.nextFrom, e.nextTo)],
  ];
}

/* "That's about 1.5 gels per week." with the amount in bold, and above it one small gel
   pouch per gel: the outline of our pouch from the dieline, body shortened so it stays compact,
   drawn as a thin stroke. Whole gels dark, the started one light grey. Up to 60 pouches.
   Layout (07.10.2026): balanced rows, at most 20 per row and never a single orphan
   (21 = 11 + 10), and a size that shrinks with the count in 1 px steps (11 px at 1 gel down to
   6 px at 60) instead of three hard steps. The grid also shrinks with a narrow container.
   snippets/rf-abo.liquid does the same integer math, so the server render matches. */
function pouchLayout(count) {
  const rows = Math.ceil(count / 20);
  const cols = Math.ceil(count / rows);
  /* Whole pixels only, so every stroke renders the same (no wobbly outlines). */
  const w = 11 - Math.round(((count - 1) * 5) / 59);
  const h = Math.round((w * 114) / 54);
  return `--pw-c:${cols};--pw-w:${w}px;--pw-h:${h}px;--pw-gx:${Math.round(w * 0.36)}px;--pw-gy:${Math.round(w * 0.55)}px`;
}
const POUCH = '<path vector-effect="non-scaling-stroke" d="M2,110H48Q50,110 50,108V32L35,23V2Q35,0 33,0H9.5Q7.5,0 7.5,2V21L0,26V108Q0,110 2,110Z"/>';
function pouchSvg(part) {
  return `<svg class="${part ? 'is-part' : ''}" viewBox="-2 -2 54 114" width="11" height="23" aria-hidden="true" focusable="false"><g fill="none" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round" stroke-linecap="round" shape-rendering="geometricPrecision">${POUCH}</g></svg>`;
}
function perWeekHtml(tpl, n, shown) {
  const esc = (v) => String(v).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
  const text = esc(tpl).replace(/\[n\](\s*\S+)/, (m, word) => `<strong>${esc(shown)}${word}</strong>`);
  let pouches = '';
  if (n > 0) {
    const whole = Math.floor(n + 1e-9);
    const part = n - whole > 0.05;
    const count = Math.min(60, whole + (part ? 1 : 0));
    for (let i = 0; i < count; i++) pouches += pouchSvg(part && i === count - 1 && count === whole + 1);
    pouches = `<span class="rf-abo__pw-viz" style="${pouchLayout(count)}" aria-hidden="true">${pouches}</span>`;
  }
  return `${pouches}<span class="rf-abo__pw-text">${text}</span>`;
}

// Two small tiles side by side: first delivery (box icon) and the next one (repeat icon).
const ETA_ICONS = [
  '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 8 12 3 3 8v8l9 5 9-5z"/><path d="m3 8 9 5 9-5M12 13v8"/></svg>',
  '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M17 2l3 3-3 3"/><path d="M4 11V9a4 4 0 0 1 4-4h12"/><path d="M7 22l-3-3 3-3"/><path d="M20 13v2a4 4 0 0 1-4 4H4"/></svg>',
];
function etaHtml(lines) {
  const esc = (v) => String(v).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
  return lines
    .map(
      ([label, date], i) =>
        `<span class="rf-abo__eta-item"><span class="rf-abo__eta-icon">${ETA_ICONS[i] || ''}</span><span class="rf-abo__eta-text"><span class="rf-abo__eta-label">${esc(label)}</span><span class="rf-abo__eta-date">${esc(date)}</span></span></span>`
    )
    .join('');
}

/* The service the dates are based on is the one the shipping box shows: on the product page
   its current main row (assets/rf-shipping.js), elsewhere the same rule for the cart: the
   cheapest service for its weight and value, then the fastest. */
let etaCart = null;
let etaCartAt = 0;
function cartForEta() {
  if (!etaCart || Date.now() - etaCartAt > 1500) {
    etaCartAt = Date.now();
    etaCart = fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .catch(() => null);
  }
  return etaCart;
}

/* Delivery estimate for the subscription: the full span of every service that ships this weight, earliest first day
   to latest last day, the same as the shipping box and the popup (21 Oct 2026; was the cheapest service only). */
function quoteDays(c, grams, eurCents) {
  let span = null;
  Object.keys(c.s).forEach((name) => {
    const svc = c.s[name];
    let price = null;
    (svc.w || []).forEach((r) => {
      if (grams >= r[0] && grams <= r[1]) price = price === null ? r[2] : Math.min(price, r[2]);
    });
    (svc.v || []).forEach((r) => {
      if (eurCents >= r[0] * 100) price = price === null ? r[1] : Math.min(price, r[1]);
    });
    if (price === null || !svc.d) return;
    span = span ? [Math.min(span[0], svc.d[0]), Math.max(span[1], svc.d[1])] : [svc.d[0], svc.d[1]];
  });
  return span;
}

async function serviceDays(ship, country) {
  const box = Array.from(document.querySelectorAll('rf-shipping')).find(
    (b) => b.main && b.country === country && b.offsetParent !== null
  );
  if (box) return box.span || box.main.days;
  const c = ship && ship.countries && ship.countries[country];
  if (!c || !c.s) return null;
  const cart = await cartForEta();
  let grams = 0;
  ((cart && cart.items) || []).forEach((it) => {
    if (it.requires_shipping === false) return;
    const g = Number(it.grams) || (ship.weights && ship.weights[it.handle]) || 0;
    grams += g * it.quantity;
  });
  const rate = (window.Shopify && window.Shopify.currency && parseFloat(window.Shopify.currency.rate)) || 1;
  const eur = Math.round(((cart && cart.total_price) || 0) / rate);
  return quoteDays(c, grams, eur);
}

function paintEta() {
  const slots = document.querySelectorAll('[data-rf-abo-eta]');
  if (!slots.length) return;
  if (!state.on) {
    /* 20 Oct 2026: write only when it changes; the body observer below listens to "hidden", so an unconditional
       write looped about 180 times a second */
    slots.forEach((el) => { if (!el.hidden) el.hidden = true; });
    return;
  }
  const weeks = state.weeks;
  loadShip().then(async (ship) => {
    const days = await serviceDays(ship, CFG.country);
    if (!state.on || weeks !== state.weeks) return;
    const e = estimate(ship, CFG.country, weeks, days);
    document.querySelectorAll('[data-rf-abo-eta]').forEach((el) => {
      if (!e) {
        if (!el.hidden) el.hidden = true;
        return;
      }
      const html = etaHtml(formatEta(e, CFG.lang === 'de' ? 'de' : 'en'));
      if (el.dataset.rfEtaHtml !== html) {
        el.innerHTML = html;
        el.dataset.rfEtaHtml = html;
      }
      if (el.hidden) el.hidden = false;
    });
  });
}

/* ---------- UI ---------- */

function paintAll() {
  document.querySelectorAll('rf-abo-toggle').forEach((el) => el.paint());
  announce();
}

/* Other parts of the page follow the Abo state (blocks/volume-tiers.liquid shows the Abo
   price in its total): window.rfAboState plus an 'rf:abo-change' event on every change. */
let announced = '';
function announce() {
  const count = state.on ? ABO_COUNT : 0;
  const key = `${state.on}|${state.weeks}|${count}`;
  window.rfAboState = { on: state.on, weeks: state.weeks, count };
  if (key === announced) return;
  announced = key;
  document.dispatchEvent(new CustomEvent('rf:abo-change', { detail: { on: state.on, weeks: state.weeks, count } }));
}

// The drawer is re-rendered after cart changes; refill the estimate in the fresh markup.
let etaQueued = false;
function queueEta() {
  if (etaQueued) return;
  etaQueued = true;
  // setTimeout, not requestAnimationFrame: rAF does not run in background tabs.
  setTimeout(() => {
    etaQueued = false;
    paintEta();
  }, 0);
}

function setBusy(busy) {
  document.querySelectorAll('rf-abo-toggle').forEach((el) => el.classList.toggle('is-busy', busy));
  document.querySelectorAll('cart-items-component').forEach((el) => el.classList.toggle('rf-abo-busy', busy));
}

/* The switch and the interval answer at once (paintAll); the cart follows in the background.
   Quick clicks (4 -> 6 -> 8) collapse into one cart sync with the latest state. */
let syncQueued = false;
function change(next) {
  const before = { ...state };
  Object.assign(state, next);
  if (before.on === state.on && before.weeks === state.weeks) return;
  paintAll();
  if (syncQueued) return;
  syncQueued = true;
  enqueue(async () => {
    syncQueued = false;
    setBusy(true);
    try {
      await sync(true);
    } finally {
      setBusy(false);
      paintAll();
    }
  });
}

/* Cart drawer: compact while Abo is on and was not opened in the drawer (picked on the product
   page). Turning it on in the drawer, or "Change", opens it; turning it off resets that. Kept
   per tab, and put back after the theme morphs the drawer (the server markup has no state). */
const DRAWER_OPEN_KEY = 'rfAboDrawerOpen';
let drawerOpen = (() => {
  try {
    return sessionStorage.getItem(DRAWER_OPEN_KEY) === '1';
  } catch (e) {
    return false;
  }
})();
function applyDrawerOpen() {
  document.querySelectorAll('rf-abo-toggle[data-context="drawer"]').forEach((el) => {
    const want = drawerOpen ? 'true' : 'false';
    if (el.dataset.expanded !== want) el.dataset.expanded = want;
    const btn = el.querySelector('[data-rf-abo-expand]');
    if (btn && btn.getAttribute('aria-expanded') !== want) btn.setAttribute('aria-expanded', want);
    el.querySelectorAll('[data-rf-abo-compact-weeks]').forEach((w) => {
      if (w.textContent !== String(state.weeks)) w.textContent = String(state.weeks);
    });
  });
}
function setDrawerOpen(open) {
  drawerOpen = open;
  try {
    sessionStorage.setItem(DRAWER_OPEN_KEY, open ? '1' : '0');
  } catch (e) {}
  applyDrawerOpen();
}

class RfAboToggle extends HTMLElement {
  connectedCallback() {
    if (!this._bound) {
      this._bound = true;
      this.addEventListener('change', (e) => {
        if (!e.target.matches('[data-rf-abo-switch]')) return;
        // In the drawer the full box stays open after switching on; picked anywhere else
        // (product page), the drawer shows the compact line.
        setDrawerOpen(this.dataset.context === 'drawer' ? e.target.checked : false);
        chosen(e.target.checked);
        change({ on: e.target.checked });
      });
      this.addEventListener('click', (e) => {
        if (this._dragged) {
          this._dragged = false;
          return;
        }
        if (e.target.closest('[data-rf-abo-collapse]')) {
          setDrawerOpen(false);
          const btn = this.querySelector('[data-rf-abo-expand]');
          if (btn) btn.focus({ preventScroll: true });
          return;
        }
        if (e.target.closest('[data-rf-abo-expand]')) {
          setDrawerOpen(true);
          const opt = this.querySelector('[data-rf-abo-weeks][aria-checked="true"]');
          if (opt) opt.focus({ preventScroll: true });
          return;
        }
        const opt = e.target.closest('[data-rf-abo-weeks]');
        if (opt) change({ weeks: Number(opt.dataset.rfAboWeeks) });
      });
      this.bindDrag();
      this.addEventListener('pointerdown', (e) => {
        if (e.target.closest('[data-rf-abo-switch], .rf-abo__row, [data-rf-abo-weeks]')) prefetchReads();
      });
    }
    this.paint();
  }

  /* The white thumb of the interval control can be dragged (mouse or finger) and snaps to
     the nearest interval on release. A plain click still picks the interval directly. */
  bindDrag() {
    this.addEventListener('pointerdown', (e) => {
      const seg = e.target.closest('.rf-abo__seg');
      if (!seg || (e.pointerType === 'mouse' && e.button !== 0)) return;
      const opts = Array.from(seg.querySelectorAll('[data-rf-abo-weeks]'));
      if (!opts.length) return;
      const box = seg.getBoundingClientRect();
      const inner = box.width - 4;
      const step = inner / opts.length;
      const current = Math.max(0, opts.findIndex((o) => o.getAttribute('aria-checked') === 'true'));
      const startX = e.clientX;
      const onThumb = Math.floor((startX - box.left - 2) / step) === current;
      let moved = false;
      const indexAt = (x) => Math.min(opts.length - 1, Math.max(0, Math.round((x - box.left - 2 - step / 2) / step)));
      const move = (ev) => {
        const dx = ev.clientX - startX;
        if (!moved && Math.abs(dx) < 4) return;
        if (!moved) {
          moved = true;
          seg.classList.add('is-dragging');
          try { seg.setPointerCapture(e.pointerId); } catch (err) {}
        }
        // From the thumb: it follows the pointer. Elsewhere: it follows the option under the pointer.
        const x = onThumb
          ? Math.min(inner - step, Math.max(0, current * step + dx))
          : indexAt(ev.clientX) * step;
        seg.style.setProperty('--rf-seg-x', `${x}px`);
      };
      const end = (ev) => {
        seg.removeEventListener('pointermove', move);
        seg.removeEventListener('pointerup', end);
        seg.removeEventListener('pointercancel', end);
        if (!moved) return;
        this._dragged = true;
        setTimeout(() => (this._dragged = false), 0);
        const x = parseFloat(seg.style.getPropertyValue('--rf-seg-x')) || 0;
        const i = ev.type === 'pointercancel' ? current : Math.min(opts.length - 1, Math.max(0, Math.round(x / step)));
        opts.forEach((o, k) => o.setAttribute('aria-checked', String(k === i)));
        seg.classList.remove('is-dragging');
        seg.style.removeProperty('--rf-seg-x');
        change({ weeks: Number(opts[i].dataset.rfAboWeeks) });
      };
      seg.addEventListener('pointermove', move);
      seg.addEventListener('pointerup', end);
      seg.addEventListener('pointercancel', end);
    });
  }

  /* Quantity picked on the product page (form quantity, else the checked volume tier). */
  pageQuantity() {
    const scope = this.closest('.shopify-section') || document;
    const input = scope.querySelector('form input[name="quantity"]');
    const fromInput = input ? parseInt(input.value, 10) : 0;
    if (fromInput > 0) return fromInput;
    const tier = scope.querySelector('.rf-vt__radio:checked');
    return (tier && parseInt(tier.value, 10)) || 1;
  }

  /* weeks -> plan id for this product: the tier counts the boxes already on the subscription
     plus the quantity picked on the page. */
  plans() {
    if (!this._planList) {
      const node = this.querySelector('[data-rf-abo-plans]');
      try {
        this._planList = (node ? JSON.parse(node.textContent) : []).filter(Boolean).map((p) => ({ ...p, opts: (p.opts || []).filter(Boolean) }));
      } catch (e) {
        this._planList = [];
      }
    }
    return planMap(this._planList, tierPct(ABO_COUNT + this.pageQuantity()));
  }

  paint() {
    const sw = this.querySelector('[data-rf-abo-switch]');
    if (sw) sw.checked = state.on;
    const more = this.querySelector('.rf-abo__more');
    if (more) more.hidden = !state.on;
    this.querySelectorAll('[data-rf-abo-weeks]').forEach((b) => {
      b.setAttribute('aria-checked', String(Number(b.dataset.rfAboWeeks) === state.weeks));
    });
    this.dataset.on = String(state.on);
    this.dataset.weeks = String(state.weeks);
    // Drawer: gels in the cart spread over the interval ("about 6 gels per week").
    const perWeek = this.querySelector('[data-rf-abo-perweek]');
    if (perWeek) {
      // Drawer: every gel in the cart. Product page: gels per unit x the chosen quantity.
      let gels = Number(this.dataset.gels) || 0;
      if (!gels && this.dataset.gelsUnit) gels = Number(this.dataset.gelsUnit) * this.pageQuantity();
      const n = gels / state.weeks;
      const lang = CFG && CFG.lang === 'de' ? 'de-DE' : 'en-GB';
      const shown = n >= 10 || Number.isInteger(n) ? Math.round(n) : Math.round(n * 10) / 10;
      perWeek.innerHTML = gels > 0 && this.dataset.tPerWeek ? perWeekHtml(this.dataset.tPerWeek, n, shown.toLocaleString(lang)) : '';
    }
    // Product page: the plan for this product goes along with the add to cart.
    const input = this.querySelector('[data-rf-abo-plan]');
    if (input) {
      const id = this.plans()[state.weeks];
      input.value = id ? String(id) : '';
      input.disabled = !(state.on && id);
    }
    queueEta();
    if (this.dataset.context === 'drawer') applyDrawerOpen();
  }
}

if (CFG && !customElements.get('rf-abo-toggle')) {
  customElements.define('rf-abo-toggle', RfAboToggle);
  // Tell the price blocks the state once at start (the quantity tiles render one-time prices
  // when the Abo was kept from the last page while the cart is empty).
  announce();

  // Product page: the "gels per week" line follows the chosen quantity.
  const repaintPdp = () =>
    document.querySelectorAll('rf-abo-toggle[data-context="pdp"]').forEach((el) => el.paint());
  ['change', 'input'].forEach((type) =>
    document.addEventListener(type, (e) => {
      if (e.target.matches && e.target.matches('.rf-vt__radio, input[name="quantity"]')) repaintPdp();
    })
  );
  document.addEventListener('click', (e) => {
    if (e.target.closest && e.target.closest('[data-rf-step], .quantity-selector button')) setTimeout(repaintPdp, 50);
  });

  // The theme morphs the cart drawer after every change, which resets the estimate to its
  // empty server markup; fill it in again (writes only on a difference, so no loop).
  new MutationObserver(() => {
    if (document.querySelector('[data-rf-abo-eta]')) queueEta();
    applyDrawerOpen();
  }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden'] });

  // Other cart changes while Abo is on: convert what was added (quick add, upsell,
  // bundles). An add from the box builder switches the Abo off instead.
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    if (!state.on || !event.promise) return;
    event.promise
      .then((result) => {
        const detail = result && result.detail;
        if (!detail || detail.source === SOURCE || detail.didError) return;
        // Cart emptied: the Abo ends with it, so nobody finds it preselected on a later visit
        // (the choice belongs to a cart, and a pre-ticked subscription is not a valid consent).
        if (detail.itemCount === 0) {
          ABO_COUNT = 0;
          chosen(false);
          setDrawerOpen(false);
          change({ on: false });
          return;
        }
        if (detail.source === BUILDER_SOURCE) {
          change({ on: false });
          return;
        }
        enqueue(() => sync(false));
      })
      .catch(() => {});
  });

  // A discount code refused while the Abo is on: the theme only says "cannot be applied".
  // Some codes are for one-time orders only, so add how to use it (switch the Abo off).
  const CODE_HINT = CFG.lang === 'de'
    ? 'Manche Codes gelten nur bei Einmalkauf. Schalte „Regelmäßig liefern“ aus, dann kannst du ihn nutzen.'
    : 'Some codes only work on one-time orders. Switch off “Regular delivery” to use it.';
  const checkCodeHint = () => {
    document.querySelectorAll('[ref="cartDiscountErrorDiscountCode"]').forEach((err) => {
      const box = err.closest('.cart-discount__error');
      let hint = box && box.querySelector('.rf-abo-codehint');
      const show = state.on && box && !box.classList.contains('hidden') && !err.classList.contains('hidden');
      if (show && !hint) {
        hint = document.createElement('small');
        hint.className = 'rf-abo-codehint';
        hint.textContent = CODE_HINT;
        err.after(hint);
      } else if (!show && hint) {
        hint.remove();
      }
    });
  };
  new MutationObserver(checkCodeHint).observe(document.body, {
    subtree: true,
    attributes: true,
    attributeFilter: ['class'],
  });
  document.addEventListener('rf:abo-change', checkCodeHint);

  // Another tab (or the back button) may have switched the Abo meanwhile: when this tab comes
  // back into view, take the state from the cart again, so adds and syncs here do not use an
  // old state (e.g. put a plan on a quick add after the Abo was switched off elsewhere).
  const refreshState = () => {
    if (document.visibilityState !== 'visible') return;
    fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((cart) => {
        const a = (cart && cart.attributes) || {};
        const on = a._rf_abo === '1' && (cart.item_count > 0 || chosen());
        const weeks = WEEKS.includes(Number(a._rf_abo_w)) ? Number(a._rf_abo_w) : state.weeks;
        if (on === state.on && weeks === state.weeks) return;
        state.on = on;
        state.weeks = weeks;
        paintAll();
      })
      .catch(() => {});
  };
  document.addEventListener('visibilitychange', refreshState);
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) refreshState();
  });

  // An empty cart still marked as Abo (emptied before this rule, or by another path): clear
  // the mark quietly, so a later add on another page does not turn into an Abo line.
  if (CFG.stale && !KEEP) {
    enqueue(() => post('update', { attributes: { _rf_abo: '0', _rf_abo_bundles: '' } }).catch(() => {}));
  }

  // A cart left in Abo mode from an earlier visit may hold lines added elsewhere.
  if (state.on) {
    enqueue(() => sync(false));
  }
  // Sale guard: the cart was in Abo mode and now holds a box on sale (the page already shows
  // the Abo off): drop the plans and the mark.
  if (CFG.saleOff && !state.on) {
    enqueue(() => sync(true));
  }

  // "Mix 4, pay 3" only for boxes that came through the bundle builder: after any other cart
  // change, the code follows the complete bundles still in the cart (see mix4Code).
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    if (!event.promise) return;
    event.promise
      .then((result) => {
        const detail = result && result.detail;
        if (!detail || detail.source === SOURCE || detail.source === BUILDER_SOURCE || detail.didError) return;
        enqueue(() => syncMix4());
      })
      .catch(() => {});
  });
  enqueue(() => syncMix4());

  // The Abo dates follow the shipping box: whenever its delivery line changes (quantity,
  // country, cart, time of day), the dates here are worked out again from the same service.
  let etaTimer = 0;
  const etaObserver = new MutationObserver(() => {
    clearTimeout(etaTimer);
    etaTimer = setTimeout(paintEta, 50);
  });
  const watchEta = () =>
    document.querySelectorAll('rf-shipping [data-rf-eta]').forEach((el) => {
      if (el.rfAboWatched) return;
      el.rfAboWatched = true;
      etaObserver.observe(el, { childList: true, characterData: true, subtree: true });
    });
  watchEta();
  if (!document.querySelector('rf-shipping [data-rf-eta]')) window.addEventListener('load', watchEta, { once: true });
}
